import React, { useState, useMemo } from "react";
import moment from "moment-hijri";
import saudiLogoSrc from "../../../images/sauid-higher-ed.svg";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { SecondAds, FirstAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";
import latness_report from "../../../images/latness-report.webp";

const TITLE = "تقرير متابعة تأخر الطلاب لجميع المراحل الدراسية";
const DESCRIPTION =
    "أنشئ تقرير متابعة تأخر الطلاب لمدرستك بسهولة. أدخل بيانات المدرسة والشهر الهجري وعدد المتأخرين يومياً لكل صف، ثم احصل على تقرير شامل مع الإحصائيات والنسب والرسوم البيانية جاهز للطباعة.";
const KEYWORDS =
    "تقرير تأخر الطلاب, متابعة تأخر الطلاب, نموذج تأخر طلاب, نسبة التأخر المدرسي, تقرير الالتزام المدرسي, وزارة التعليم السعودية, تقرير شهري مدرسة";
const OG_URL =
    "https://do-calculate.com/calculator/ar/student-lateness-report/";

const HIJRI_MONTHS = [
    "محرم", "صفر", "ربيع الأول", "ربيع الثاني",
    "جمادى الأولى", "جمادى الثانية", "رجب", "شعبان",
    "رمضان", "شوال", "ذو القعدة", "ذو الحجة",
];

const GREGORIAN_MONTHS = [
    { label: "كانون الثاني (يناير)", common: "يناير" },
    { label: "شباط (فبراير)", common: "فبراير" },
    { label: "آذار (مارس)", common: "مارس" },
    { label: "نيسان (أبريل)", common: "أبريل" },
    { label: "أيار (مايو)", common: "مايو" },
    { label: "حزيران (يونيو)", common: "يونيو" },
    { label: "تموز (يوليو)", common: "يوليو" },
    { label: "آب (أغسطس)", common: "أغسطس" },
    { label: "أيلول (سبتمبر)", common: "سبتمبر" },
    { label: "تشرين الأول (أكتوبر)", common: "أكتوبر" },
    { label: "تشرين الثاني (نوفمبر)", common: "نوفمبر" },
    { label: "كانون الأول (ديسمبر)", common: "ديسمبر" },
];

const DAY_NAMES_AR = {
    0: "الأحد", 1: "الإثنين", 2: "الثلاثاء", 3: "الأربعاء", 4: "الخميس",
};

const DAY_COLORS = {
    "الأحد": "#1565c0",
    "الإثنين": "#0d47a1",
    "الثلاثاء": "#1976d2",
    "الأربعاء": "#212121",
    "الخميس": "#c9a227",
};

const STAGES = {
    "رياض الأطفال": { label: "رياض الأطفال", grades: [-2, -1, 0] },
    ابتدائية: { label: "المرحلة الابتدائية", grades: [1, 2, 3, 4, 5, 6] },
    متوسطة: { label: "المرحلة المتوسطة", grades: [7, 8, 9] },
    ثانوية: { label: "المرحلة الثانوية", grades: [10, 11, 12] },
};

const GRADE_NAMES = {
    "-2": "مستوى أول", "-1": "مستوى ثاني", 0: "مستوى ثالث",
    1: "الأول", 2: "الثاني", 3: "الثالث", 4: "الرابع", 5: "الخامس", 6: "السادس",
    7: "السابع", 8: "الثامن", 9: "التاسع",
    10: "العاشر", 11: "الحادي عشر", 12: "الثاني عشر",
};

function getWorkingDaysHijri(hijriYear, hijriMonth) {
    const daysInMonth = moment(`${hijriYear}/${hijriMonth}/1`, "iYYYY/iM/iD").iDaysInMonth();
    const days = [];
    for (let d = 1; d <= daysInMonth; d++) {
        const m = moment(`${hijriYear}/${hijriMonth}/${d}`, "iYYYY/iM/iD");
        const dow = m.day();
        if (dow !== 5 && dow !== 6) {
            days.push({ day: d, month: hijriMonth, arabicDay: DAY_NAMES_AR[dow] });
        }
    }
    return days;
}

function getWorkingDaysGregorian(gregYear, gregMonth) {
    // gregMonth is 1-based
    const daysInMonth = new Date(gregYear, gregMonth, 0).getDate();
    const days = [];
    for (let d = 1; d <= daysInMonth; d++) {
        const jsDay = new Date(gregYear, gregMonth - 1, d).getDay();
        if (jsDay !== 5 && jsDay !== 6) {
            days.push({ day: d, month: gregMonth, arabicDay: DAY_NAMES_AR[jsDay === 0 ? 0 : jsDay] });
        }
    }
    return days;
}

function fmt(num, d = 2) {
    return parseFloat(num).toFixed(d);
}

function getComplianceLevel(rate) {
    if (rate >= 95) return "عالٍ";
    if (rate >= 85) return "متوسط";
    return "منخفض";
}

// Inject temporary print CSS, print, then remove
function printReport() {
    const printable = document.getElementById("report-printable");
    if (!printable) return;
    // Fix relative image src to absolute so they load in the blank window
    const clone = printable.cloneNode(true);
    clone.querySelectorAll("img").forEach((img) => {
        img.src = img.src; // forces absolute URL resolution
    });
    const content = clone.innerHTML;
    const win = window.open("", "_blank");
    win.document.write(`
        <!DOCTYPE html>
        <html dir="rtl" lang="ar">
        <head>
            <meta charset="UTF-8" />
            <title>تقرير متابعة التأخر</title>
            <style>
                *, *::before, *::after { box-sizing: border-box; }
                body {
                    font-family: Arial, Tahoma, sans-serif;
                    direction: rtl;
                    margin: 20px;
                    font-size: 13px;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                    color-adjust: exact !important;
                }
                table { width: 100%; border-collapse: collapse; font-size: 11px; }
                th { background-color: #f5f5f5 !important; color: #000 !important; font-weight: bold; border: 1px solid #2e2e2e; padding: 5px; text-align: center; }
                td { border: 1px solid #2e2e2e; padding: 5px; text-align: center; }
                h2, h3 { text-align: center; }
                /* Force all background colors and gradients to print */
                * {
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                    color-adjust: exact !important;
                }
                @media print {
                    @page { margin: 0.5cm; size: A4 portrait; }
                    body { margin: 5px; font-size: 11px; }
                    table { font-size: 9px; }
                    th, td { padding: 3px !important; }
                    h2 { font-size: 13px; margin: 4px 0; }
                    h3 { font-size: 12px; margin: 4px 0; }
                    hr { margin: 6px 0; }
                    /* Keep charts and stats together, no page break inside */
                    div { page-break-inside: avoid; }
                }
            </style>
        </head>
        <body>${content}</body>
        </html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => {
        win.print();
        win.close();
    }, 500);
}

// ─── Mini bar chart (CSS only) ───────────────────────────────────────────────
function BarChart({ grades, gradeTotals }) {
    return (
        <div>
            <div
                style={{
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "center",
                    gap: "28px",
                    height: "140px",
                    borderBottom: "1px solid #ccc",
                    padding: "0 8px",
                }}
            >
                {grades.map((g) => {
                    const tard = gradeTotals[g].rate;
                    const comp = 100 - tard;
                    const compH = Math.max((comp / 100) * 120, 4);
                    const tardH = Math.max((tard / 100) * 120, 2);
                    return (
                        <div
                            key={g}
                            style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "flex-end",
                                    gap: "3px",
                                    height: "130px",
                                }}
                            >
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                    <span style={{ fontSize: "10px", marginBottom: "2px" }}>
                                        {fmt(comp, 1)}%
                                    </span>
                                    <div style={{ width: "22px", height: `${compH}px`, backgroundColor: "#1e3a5f" }} />
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                    <span style={{ fontSize: "10px", marginBottom: "2px" }}>
                                        {fmt(tard, 1)}%
                                    </span>
                                    <div style={{ width: "22px", height: `${tardH}px`, backgroundColor: "#c9a227" }} />
                                </div>
                            </div>
                            <div style={{ fontSize: "11px", marginTop: "4px", textAlign: "center" }}>
                                الصف {GRADE_NAMES[g]}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "20px",
                    marginTop: "8px",
                    fontSize: "12px",
                }}
            >
                <span>
                    <span
                        style={{
                            display: "inline-block",
                            width: "12px",
                            height: "12px",
                            backgroundColor: "#1e3a5f",
                            marginLeft: "4px",
                            verticalAlign: "middle",
                        }}
                    />
                    الالتزام
                </span>
                <span>
                    <span
                        style={{
                            display: "inline-block",
                            width: "12px",
                            height: "12px",
                            backgroundColor: "#c9a227",
                            marginLeft: "4px",
                            verticalAlign: "middle",
                        }}
                    />
                    التأخر
                </span>
            </div>
        </div>
    );
}

// ─── Mini pie chart (CSS conic-gradient) ─────────────────────────────────────
function PieChart({ overallRate, complianceRate }) {
    const t = Math.min(Math.max(overallRate, 0), 100);
    return (
        <div style={{ textAlign: "center" }}>
            <div
                style={{
                    width: "130px",
                    height: "130px",
                    borderRadius: "50%",
                    background: `conic-gradient(#c9a227 0% ${t}%, #1e3a5f ${t}% 100%)`,
                    margin: "0 auto 10px",
                }}
            />
            <div style={{ fontSize: "12px", lineHeight: "1.8" }}>
                <div>
                    <span
                        style={{
                            display: "inline-block",
                            width: "12px",
                            height: "12px",
                            backgroundColor: "#1e3a5f",
                            marginLeft: "4px",
                            verticalAlign: "middle",
                        }}
                    />
                    الالتزام {fmt(complianceRate)}%
                </div>
                <div>
                    <span
                        style={{
                            display: "inline-block",
                            width: "12px",
                            height: "12px",
                            backgroundColor: "#c9a227",
                            marginLeft: "4px",
                            verticalAlign: "middle",
                        }}
                    />
                    التأخر {fmt(overallRate)}%
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function StudentLatenessReport() {
    // Form fields
    const [schoolType, setSchoolType] = useState("مدرسة بنين");
    const [term, setTerm] = useState("الفصل الدراسي الأول");
    const [administration, setAdministration] = useState("");
    const [school, setSchool] = useState("");
    const [principal, setPrincipal] = useState("");
    const [responsible, setResponsible] = useState("");
    const [dateMode, setDateMode] = useState("hijri"); // "hijri" | "gregorian"
    const [hijriYear, setHijriYear] = useState(1447);
    const [hijriMonth, setHijriMonth] = useState(4);
    const [gregYear, setGregYear] = useState(2026);
    const [gregMonth, setGregMonth] = useState(4);
    const [stage, setStage] = useState("ابتدائية");
    const [selectedGrades, setSelectedGrades] = useState([]);
    const [gradeCounts, setGradeCounts] = useState({});

    // Steps: 1 = form, 2 = table entry, 3 = report
    const [step, setStep] = useState(1);

    const [workingDays, setWorkingDays] = useState([]);
    const [latenessData, setLatenessData] = useState({}); // { dayIdx: { grade: count } }

    const stageGrades = STAGES[stage]?.grades || [];
    const sortedGrades = useMemo(
        () => [...selectedGrades].sort((a, b) => a - b),
        [selectedGrades]
    );

    const toggleGrade = (g) => {
        setSelectedGrades((prev) =>
            prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]
        );
    };

    const setGradeCount = (g, v) =>
        setGradeCounts((prev) => ({ ...prev, [g]: v }));

    const handleShowTable = () => {
        if (selectedGrades.length === 0) return;
        const days = dateMode === "hijri"
            ? getWorkingDaysHijri(hijriYear, hijriMonth)
            : getWorkingDaysGregorian(gregYear, gregMonth);
        setWorkingDays(days);
        const init = {};
        days.forEach((_, i) => {
            init[i] = {};
            sortedGrades.forEach((g) => { init[i][g] = ""; });
        });
        setLatenessData(init);
        setStep(2);
    };

    const setLate = (dayIdx, grade, value) =>
        setLatenessData((prev) => ({
            ...prev,
            [dayIdx]: { ...prev[dayIdx], [grade]: value },
        }));

    const isLateInvalid = (dayIdx, grade) => {
        const val = parseInt(latenessData[dayIdx]?.[grade]);
        const max = parseInt(gradeCounts[grade]);
        return !isNaN(val) && !isNaN(max) && max > 0 && val > max;
    };

    const handleClearForm = () => {
        setSchoolType("مدرسة بنين");
        setTerm("الفصل الدراسي الأول");
        setAdministration("");
        setSchool("");
        setPrincipal("");
        setResponsible("");
        setDateMode("hijri");
        setHijriYear(1447);
        setHijriMonth(4);
        setGregYear(2026);
        setGregMonth(4);
        setStage("ابتدائية");
        setSelectedGrades([]);
        setGradeCounts({});
    };

    const handleClearTable = () => {
        const init = {};
        workingDays.forEach((_, i) => {
            init[i] = {};
            sortedGrades.forEach((g) => { init[i][g] = ""; });
        });
        setLatenessData(init);
    };

    // ── Report computations ────────────────────────────────────────────────────
    const reportData = useMemo(() => {
        if (step !== 3 || sortedGrades.length === 0) return null;
        const schoolDays = workingDays.length;

        const dayResults = workingDays.map((day, i) => {
            let dayLate = 0;
            const gradeLate = {};
            const gradeRate = {};
            sortedGrades.forEach((g) => {
                const late = parseInt(latenessData[i]?.[g]) || 0;
                const total = parseInt(gradeCounts[g]) || 0;
                gradeLate[g] = late;
                gradeRate[g] = total > 0 ? (late / total) * 100 : 0;
                dayLate += late;
            });
            const allStudents = sortedGrades.reduce(
                (s, g) => s + (parseInt(gradeCounts[g]) || 0),
                0
            );
            return {
                day, gradeLate, gradeRate,
                totalLate: dayLate,
                totalRate: allStudents > 0 ? (dayLate / allStudents) * 100 : 0,
            };
        });

        const gradeTotals = {};
        sortedGrades.forEach((g) => {
            const total = parseInt(gradeCounts[g]) || 0;
            const totalLate = workingDays.reduce(
                (s, _, i) => s + (parseInt(latenessData[i]?.[g]) || 0),
                0
            );
            gradeTotals[g] = {
                late: totalLate,
                rate: total * schoolDays > 0 ? (totalLate / (total * schoolDays)) * 100 : 0,
            };
        });

        const totalStudents = sortedGrades.reduce(
            (s, g) => s + (parseInt(gradeCounts[g]) || 0),
            0
        );
        const totalLateAll = sortedGrades.reduce(
            (s, g) => s + gradeTotals[g].late,
            0
        );
        const overallRate =
            totalStudents * schoolDays > 0
                ? (totalLateAll / (totalStudents * schoolDays)) * 100
                : 0;
        const complianceRate = 100 - overallRate;

        return {
            schoolDays, dayResults, gradeTotals,
            totalStudents, totalLateAll, overallRate, complianceRate,
            level: getComplianceLevel(complianceRate),
        };
    }, [step, sortedGrades, workingDays, latenessData, gradeCounts]);

    const academicYear = dateMode === "hijri" ? `${hijriYear}-${hijriYear + 1}` : `${gregYear}`;
    const monthName = dateMode === "hijri"
        ? HIJRI_MONTHS[hijriMonth - 1]
        : GREGORIAN_MONTHS[gregMonth - 1].common;
    const calSuffix = dateMode === "hijri" ? "هـ" : "م";
    const reportTitle = dateMode === "hijri"
        ? `تقرير متابعة التأخر للـ${term} / لشهر ${monthName} للعام الدراسي ${academicYear} ${calSuffix}`
        : `تقرير متابعة التأخر للـ${term} / لشهر ${monthName} ${academicYear} ${calSuffix}`;
    const pageTitle = `تقرير متابعة تأخر الطلاب لجميع المراحل الدراسية`;

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <div className="rtl" style={{ direction: "rtl" }}>
            <style>{`
                .rtl table.student-report-table th {
                    background: inherit !important;
                    color: inherit !important;
                    font-weight: bold !important;
                    padding: 8px !important;
                }
            `}</style>
            <SharedHelmet
                TITLE={TITLE}
                DESCRIPTION={DESCRIPTION}
                KEYWORDS={KEYWORDS}
                OG_URL={OG_URL}
            />

            <div className="row">
                <div className="col-12 col-md-8">
                    <FirstAds />
                </div>
                <div className="col-12 col-md-4">
                    <img src={latness_report} alt="تقرير متابعة التأخر" style={{height:'300px', width:'100%'}} />
                </div>
            </div>

            <div className="row">
                {/* ── Main column ── */}
                <div className="col-12 col-md-8">
                    <br />
                    <div
                        className="bg-success text-white text-center"
                        style={{ padding: "15px", borderRadius: "5px" }}
                    >
                        <h1 style={{ fontSize: "20px", margin: 0 }}>{pageTitle}</h1>
                    </div>
                    <p>
                        تمكنك هذه الأداة من إنشاء تقرير متابعة تأخر الطلاب لمدرستك بسهولة. ما عليك سوى إدخال بيانات المدرسة والشهر الهجري وعدد المتأخرين يومياً لكل صف، ثم ستحصل على تقرير شامل يتضمن إحصائيات ونسب ورسوم بيانية جاهزة للطباعة. هذا التقرير يساعدك في فهم مدى الالتزام المدرسي واتخاذ الإجراءات اللازمة لتحسينه.
                        كما أيضاً يمكنك حفظ التقرير أو طباعته مباشرة لاستخدامه في اجتماعات المتابعة أو تقديمه لوزارة التعليم. ابدأ الآن بإدخال بيانات مدرستك وشهر المتابعة للحصول على تقرير مفصل وشامل!
                    </p>

                    <div className="border border-light mt-3 p-3 bg-white shadow-sm">
                        <MobileUnderCalAds />

                        {/* ════════════════ STEP 1: FORM ════════════════ */}
                        {step === 1 && (
                            <div className="calculatorStyle mb-4">
                                <div className="p-3 mb-3 bg-dark text-white text-center rounded">
                                    <h2 style={{ fontSize: "17px", margin: 0 }}>بيانات التقرير</h2>
                                </div>

                                {/* Alert note */}
                                <div
                                    className="alert alert-warning mb-3"
                                    style={{ fontSize: "13px" }}
                                >
                                    ملاحظة: اختر <strong>الصفوف</strong> لتقرير جاهز حسب المرحلة الدراسية، وأدخل
                                    عدد الطلاب لكل صف.
                                </div>

                                <div className="row">
                                    <div className="col-sm-12 col-md-6 mb-3">
                                        <label>نوع المدرسة</label>
                                        <select
                                            className="form-select"
                                            value={schoolType}
                                            onChange={(e) => setSchoolType(e.target.value)}
                                        >
                                            {["مدرسة بنين", "مدرسة بنات", "مختلطة"].map((t) => (
                                                <option key={t}>{t}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-sm-12 col-md-6 mb-3">
                                        <label>الفصل الدراسي</label>
                                        <select
                                            className="form-select"
                                            value={term}
                                            onChange={(e) => setTerm(e.target.value)}
                                        >
                                            {[
                                                "الفصل الدراسي الأول",
                                                "الفصل الدراسي الثاني",
                                            ].map((t) => (
                                                <option key={t}>{t}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-sm-12 col-md-6 mb-3">
                                        <label>الإدارة</label>
                                        <input
                                            className="form-control"
                                            value={administration}
                                            onChange={(e) => setAdministration(e.target.value)}
                                            placeholder="اسم الإدارة التعليمية"
                                        />
                                    </div>
                                    <div className="col-sm-12 col-md-6 mb-3">
                                        <label>المدرسة</label>
                                        <input
                                            className="form-control"
                                            value={school}
                                            onChange={(e) => setSchool(e.target.value)}
                                            placeholder="اسم المدرسة"
                                        />
                                    </div>
                                    <div className="col-sm-12 col-md-6 mb-3">
                                        <label>مدير المدرسة</label>
                                        <input
                                            className="form-control"
                                            value={principal}
                                            onChange={(e) => setPrincipal(e.target.value)}
                                            placeholder="اسم مدير المدرسة"
                                        />
                                    </div>
                                    <div className="col-sm-12 col-md-6 mb-3">
                                        <label>الموظف المسؤول</label>
                                        <input
                                            className="form-control"
                                            value={responsible}
                                            onChange={(e) => setResponsible(e.target.value)}
                                            placeholder="اسم الموظف المسؤول"
                                        />
                                    </div>
                                    <div className="col-sm-12 col-md-6 mb-3">
                                        <label>نوع التاريخ</label>
                                        <select
                                            className="form-select"
                                            value={dateMode}
                                            onChange={(e) => setDateMode(e.target.value)}
                                        >
                                            <option value="hijri">هجري</option>
                                            <option value="gregorian">ميلادي</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Date pickers — conditional on dateMode */}
                                <div className="row">
                                    {dateMode === "hijri" ? (
                                        <>
                                            <div className="col-sm-12 col-md-6 mb-3">
                                                <label>العام الهجري</label>
                                                <select
                                                    className="form-select"
                                                    value={hijriYear}
                                                    onChange={(e) => setHijriYear(parseInt(e.target.value))}
                                                >
                                                    {[1445, 1446, 1447, 1448, 1449, 1450].map((y) => (
                                                        <option key={y} value={y}>{y}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-sm-12 col-md-6 mb-3">
                                                <label>الشهر الهجري</label>
                                                <select
                                                    className="form-select"
                                                    value={hijriMonth}
                                                    onChange={(e) => setHijriMonth(parseInt(e.target.value))}
                                                >
                                                    {HIJRI_MONTHS.map((m, i) => (
                                                        <option key={i + 1} value={i + 1}>{m} ({i + 1})</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="col-sm-12 col-md-6 mb-3">
                                                <label>السنة الميلادية</label>
                                                <select
                                                    className="form-select"
                                                    value={gregYear}
                                                    onChange={(e) => setGregYear(parseInt(e.target.value))}
                                                >
                                                    {[2024, 2025, 2026, 2027, 2028].map((y) => (
                                                        <option key={y} value={y}>{y}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-sm-12 col-md-6 mb-3">
                                                <label>الشهر الميلادي</label>
                                                <select
                                                    className="form-select"
                                                    value={gregMonth}
                                                    onChange={(e) => setGregMonth(parseInt(e.target.value))}
                                                >
                                                    {GREGORIAN_MONTHS.map((m, i) => (
                                                        <option key={i + 1} value={i + 1}>{m.label}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </>
                                    )}
                                </div>

                                <hr />

                                {/* Stage + grade selection */}
                                <div className="mb-3">
                                    <label>اختيار المرحلة الدراسية</label>
                                    <select
                                        className="form-select"
                                        value={stage}
                                        onChange={(e) => {
                                            setStage(e.target.value);
                                            setSelectedGrades([]);
                                            setGradeCounts({});
                                        }}
                                    >
                                        {Object.entries(STAGES).map(([key, val]) => (
                                            <option key={key} value={key}>
                                                {val.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="mb-3">
                                    <label>اختيار الصفوف وعدد الطلاب:</label>
                                    <div className="row mt-2">
                                        {stageGrades.map((g) => (
                                            <div
                                                key={g}
                                                className="col-sm-12 col-md-6 mb-3"
                                                style={{ display: "flex", alignItems: "center", gap: "10px" }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    id={`grade-${g}`}
                                                    checked={selectedGrades.includes(g)}
                                                    onChange={() => toggleGrade(g)}
                                                    style={{ accentColor: "#198754", width: "18px", height: "18px", flexShrink: 0 }}
                                                />
                                                <label
                                                    htmlFor={`grade-${g}`}
                                                    style={{ margin: 0, minWidth: "110px", cursor: "pointer", flexShrink: 0 }}
                                                >
                                                    🎓 الصف {GRADE_NAMES[g]}
                                                </label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    placeholder="عدد الطلاب"
                                                    style={{ textAlign: "center", flex: 1 }}
                                                    value={gradeCounts[g] || ""}
                                                    onChange={(e) => setGradeCount(g, e.target.value)}
                                                    disabled={!selectedGrades.includes(g)}
                                                    min="1"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="text-center mt-3" style={{ display: "flex", justifyContent: "center", gap: "16px" }}>
                                    <button
                                        className="btn btn-secondary"
                                        style={{ fontSize: "15px", padding: "10px 24px" }}
                                        onClick={handleClearForm}
                                    >
                                        مسح بيانات التقرير
                                    </button>
                                    <button
                                        className="btn btn-success"
                                        style={{ fontSize: "16px", padding: "10px 40px" }}
                                        onClick={handleShowTable}
                                        disabled={selectedGrades.length === 0}
                                    >
                                        عرض جدول التأخر
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ════════════════ STEP 2: TABLE ENTRY ════════════════ */}
                        {step === 2 && (
                            <div className="calculatorStyle mb-4">
                                <div
                                    className="p-3 mb-3 text-center rounded"
                                    style={{ backgroundColor: "#1e3a5f", border: "2px solid #1e3a5f" }}
                                >
                                    <h2 style={{ fontSize: "17px", margin: 0, color: "#ffffff" }}>جدول التأخر</h2>
                                    <p style={{ fontSize: "13px", margin: "4px 0 0", color: "#e0e8f4" }}>
                                        {monthName} {dateMode === "hijri" ? hijriYear : gregYear} — أدخل عدد المتأخرين لكل يوم وصف
                                    </p>
                                </div>

                                <div style={{ overflowX: "auto" }}>
                                    <table
                                        className="table table-bordered"
                                        style={{ fontSize: "14px", minWidth: "400px" }}
                                    >
                                        <thead>
                                            <tr
                                                style={{
                                                    backgroundColor: "#1e3a5f",
                                                    color: "white",
                                                    textAlign: "center",
                                                }}
                                            >
                                                <th style={{ verticalAlign: "middle" }}>اليوم</th>
                                                <th style={{ verticalAlign: "middle" }}>{dateMode === "hijri" ? "التاريخ الهجري" : "التاريخ الميلادي"}</th>
                                                {sortedGrades.map((g) => (
                                                    <th key={g} style={{ verticalAlign: "middle" }}>
                                                        الصف {GRADE_NAMES[g]}
                                                        <br />
                                                        <small style={{ fontWeight: "normal" }}>المتأخرين</small>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {workingDays.map((d, i) => (
                                                <tr
                                                    key={i}
                                                    style={{
                                                        backgroundColor: i % 2 === 0 ? "#f8f9fa" : "white",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    <td
                                                        style={{ color: "#c9a227", fontWeight: "bold" }}
                                                    >
                                                        {d.arabicDay}
                                                    </td>
                                                    <td>
                                                        {d.month}/{d.day}
                                                    </td>
                                                    {sortedGrades.map((g) => (
                                                        <td key={g} style={{ padding: "4px 6px" }}>
                                                            <input
                                                                type="number"
                                                                style={{
                                                                    width: "70px",
                                                                    textAlign: "center",
                                                                    border: isLateInvalid(i, g) ? "2px solid #dc3545" : "1px solid #ced4da",
                                                                    borderRadius: "4px",
                                                                    padding: "4px",
                                                                    backgroundColor: isLateInvalid(i, g) ? "#fff5f5" : "white",
                                                                }}
                                                                title={isLateInvalid(i, g) ? `عدد الطلاب ${gradeCounts[g]} طالب` : ""}
                                                                value={latenessData[i]?.[g] ?? ""}
                                                                onChange={(e) => setLate(i, g, e.target.value)}
                                                                min="0"
                                                            />
                                                            {isLateInvalid(i, g) && (
                                                                <div style={{ fontSize: "10px", color: "#dc3545", marginTop: "2px" }}>
                                                                    عدد الطلاب: {gradeCounts[g]}
                                                                </div>
                                                            )}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div
                                    className="text-center mt-3"
                                    style={{ display: "flex", justifyContent: "center", gap: "16px" }}
                                >
                                    <button
                                        className="btn"
                                        style={{ padding: "10px 24px", backgroundColor: "#0d6efd", color: "white", border: "none" }}
                                        onClick={() => setStep(1)}
                                    >
                                        رجوع
                                    </button>
                                    <button
                                        className="btn btn-secondary"
                                        style={{ padding: "10px 24px" }}
                                        onClick={handleClearTable}
                                    >
                                        مسح الجدول
                                    </button>
                                    <button
                                        className="btn no-print"
                                        style={{
                                            fontSize: "16px",
                                            padding: "10px 40px",
                                            backgroundColor: "#1e3a5f",
                                            color: "white",
                                        }}
                                        onClick={() => setStep(3)}
                                        disabled={workingDays.some((_, i) =>
                                            sortedGrades.some((g) => isLateInvalid(i, g))
                                        )}
                                        title={workingDays.some((_, i) => sortedGrades.some((g) => isLateInvalid(i, g))) ? "يوجد قيم تتجاوز عدد الطلاب — يرجى التصحيح أولاً" : ""}
                                    >
                                        إنشاء التقرير
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ════════════════ STEP 3: REPORT ════════════════ */}
                        {step === 3 && reportData && (
                            <div className="calculatorStyle mb-4">
                                {/* ── Printable section ── */}
                                <div id="report-printable" style={{ direction: "rtl" }}>
                                    {/* Header */}
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "flex-start",
                                            marginBottom: "12px",
                                        }}
                                    >
                                        <div style={{ fontSize: "13px", lineHeight: "1.8" }}>
                                            <strong>وزارة التعليم</strong>
                                            {administration && <div>{administration}</div>}
                                            {school && <div>{school}</div>}
                                        </div>
                                        <div style={{ textAlign: "center" }}>
                                            <img src={saudiLogoSrc} alt="وزارة التعليم" style={{ width: "70px", height: "70px" }} />
                                        </div>
                                        <div style={{ fontSize: "13px", lineHeight: "1.8", textAlign: "left" }}>
                                            {schoolType}
                                            <br />
                                            {term}
                                        </div>
                                    </div>

                                    <h2
                                        style={{
                                            textAlign: "center",
                                            fontSize: "15px",
                                            borderBottom: "2px solid #1e3a5f",
                                            paddingBottom: "8px",
                                            marginBottom: "16px",
                                        }}
                                    >
                                        {reportTitle}
                                    </h2>

                                    {/* ── Main report table ── */}
                                    <div style={{ overflowX: "auto" }}>
                                        <table
                                            className="table table-bordered student-report-table"
                                            style={{ width: "100%", fontSize: "13px", borderCollapse: "collapse" }}
                                        >
                                            <thead>
                                                <tr style={{ textAlign: "center" }}>
                                                    <th rowSpan={2} style={{ verticalAlign: "middle", backgroundColor: "#f5f5f5", color: "#000", border: "1px solid #2e2e2e", padding: "4px" }}>
                                                        اليوم
                                                    </th>
                                                    <th rowSpan={2} style={{ verticalAlign: "middle", backgroundColor: "#f5f5f5", color: "#000", border: "1px solid #2e2e2e", padding: "4px" }}>
                                                        {dateMode === "hijri" ? "التاريخ الهجري" : "التاريخ الميلادي"}
                                                    </th>
                                                    {sortedGrades.map((g) => (
                                                        <th key={g} colSpan={2} style={{ textAlign: "center", backgroundColor: "#f5f5f5", color: "#000", border: "1px solid #2e2e2e", padding: "4px" }}>
                                                            الصف {GRADE_NAMES[g]}
                                                        </th>
                                                    ))}
                                                    <th colSpan={2} style={{ textAlign: "center", backgroundColor: "#f5f5f5", color: "#000", border: "1px solid #2e2e2e", padding: "4px" }}>
                                                        ملخص اليوم لجميع الصفوف/الفصول
                                                    </th>
                                                </tr>
                                                <tr style={{ textAlign: "center" }}>
                                                    {sortedGrades.map((g) => (
                                                        <React.Fragment key={g}>
                                                            <th style={{ backgroundColor: "#f5f5f5", color: "#000", border: "1px solid #2e2e2e", padding: "4px" }}>المتأخرين</th>
                                                            <th style={{ backgroundColor: "#f5f5f5", color: "#000", border: "1px solid #2e2e2e", padding: "4px" }}>نسبة التأخر</th>
                                                        </React.Fragment>
                                                    ))}
                                                    <th style={{ backgroundColor: "#f5f5f5", color: "#000", border: "1px solid #2e2e2e", padding: "4px" }}>المتأخرين</th>
                                                    <th style={{ backgroundColor: "#f5f5f5", color: "#000", border: "1px solid #2e2e2e", padding: "4px" }}>نسبة التأخر</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {reportData.dayResults.map((row, i) => (
                                                    <tr key={i} style={{ textAlign: "center" }}>
                                                        <td style={{ border: "1px solid #2e2e2e", padding: "4px" }}>
                                                            {row.day.arabicDay}
                                                        </td>
                                                        <td style={{ border: "1px solid #2e2e2e", padding: "4px" }}>
                                                            {row.day.month}/{row.day.day}
                                                        </td>
                                                        {sortedGrades.map((g) => (
                                                            <React.Fragment key={g}>
                                                                <td style={{ border: "1px solid #2e2e2e", padding: "4px" }}>{row.gradeLate[g]}</td>
                                                                <td style={{ border: "1px solid #2e2e2e", padding: "4px" }}>{fmt(row.gradeRate[g])}%</td>
                                                            </React.Fragment>
                                                        ))}
                                                        <td style={{ border: "1px solid #2e2e2e", padding: "4px" }}>
                                                            {row.totalLate}
                                                        </td>
                                                        <td style={{ border: "1px solid #2e2e2e", padding: "4px" }}>
                                                            {fmt(row.totalRate)}%
                                                        </td>
                                                    </tr>
                                                ))}
                                                {/* Totals row */}
                                                <tr style={{ textAlign: "center", fontWeight: "bold" }}>
                                                    <td colSpan={2} style={{ border: "1px solid #2e2e2e", padding: "4px" }}>الإجمالي</td>
                                                    {sortedGrades.map((g) => (
                                                        <React.Fragment key={g}>
                                                            <td style={{ border: "1px solid #2e2e2e", padding: "4px" }}>{reportData.gradeTotals[g].late}</td>
                                                            <td style={{ border: "1px solid #2e2e2e", padding: "4px" }}>{fmt(reportData.gradeTotals[g].rate)}%</td>
                                                        </React.Fragment>
                                                    ))}
                                                    <td style={{ border: "1px solid #2e2e2e", padding: "4px" }}>{reportData.totalLateAll}</td>
                                                    <td style={{ border: "1px solid #2e2e2e", padding: "4px" }}>{fmt(reportData.overallRate)}%</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Signatures (row 1) */}
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            margin: "12px 0",
                                            fontSize: "13px",
                                        }}
                                    >
                                        {responsible && (
                                            <div>
                                                <strong>الموظف المسؤول</strong>
                                                <br />
                                                {responsible}
                                            </div>
                                        )}
                                        {principal && (
                                            <div style={{ textAlign: "left" }}>
                                                <strong>مدير المدرسة</strong>
                                                <br />
                                                {principal}
                                            </div>
                                        )}
                                    </div>

                                    <hr style={{ borderColor: "#1e3a5f", borderWidth: "2px" }} />

                                    {/* ── Stats section ── */}
                                    <h3
                                        style={{
                                            textAlign: "center",
                                            fontSize: "15px",
                                            borderBottom: "2px solid #1e3a5f",
                                            paddingBottom: "8px",
                                            marginBottom: "16px",
                                        }}
                                    >
                                        {reportTitle}
                                    </h3>

                                    {/* Stats cards — row 1: 3 stats */}
                                    <div style={{ display: "flex", justifyContent: "space-around", textAlign: "center", marginBottom: "12px" }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: "28px", fontWeight: "bold", color: "#1e3a5f" }}>
                                                {reportData.totalStudents}
                                            </div>
                                            <div style={{ fontSize: "12px" }}>👥 عدد الطلاب الكلي</div>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: "28px", fontWeight: "bold", color: "#1e3a5f" }}>
                                                {sortedGrades.length}
                                            </div>
                                            <div style={{ fontSize: "12px" }}>🎓 عدد الصفوف/الفصول</div>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: "28px", fontWeight: "bold", color: "#1e3a5f" }}>
                                                {reportData.schoolDays}
                                            </div>
                                            <div style={{ fontSize: "12px" }}>📅 عدد أيام الدراسة الفعلي</div>
                                        </div>
                                    </div>

                                    {/* Stats cards — row 2: compliance level + rates */}
                                    <div style={{ display: "flex", justifyContent: "space-around", textAlign: "center", marginBottom: "16px" }}>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: "18px", fontWeight: "bold", color: "#155724" }}>
                                                ↑ {reportData.level}
                                            </div>
                                            <div style={{ fontSize: "12px", color: "#155724" }}>مستوى الالتزام</div>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: "20px", fontWeight: "bold", color: "#c9a227" }}>
                                                {fmt(reportData.overallRate)}%
                                            </div>
                                            <div style={{ fontSize: "12px" }}>نسبة التأخر</div>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: "20px", fontWeight: "bold", color: "#155724" }}>
                                                {fmt(reportData.complianceRate)}%
                                            </div>
                                            <div style={{ fontSize: "12px" }}>نسبة الالتزام</div>
                                        </div>
                                    </div>

                                    {/* Charts — side by side like image 1 */}
                                    <div style={{ display: "flex", gap: "16px", alignItems: "flex-start" }}>
                                        <div style={{ flex: "0 0 58%" }}>
                                            <div style={{ textAlign: "center", fontSize: "13px", fontWeight: "bold", marginBottom: "8px" }}>
                                                نسبة التأخر والالتزام لكل صف / فصل
                                            </div>
                                            <br />
                                            <BarChart
                                                grades={sortedGrades}
                                                gradeTotals={reportData.gradeTotals}
                                            />
                                        </div>
                                        <div style={{ flex: "0 0 38%" }}>
                                            <div style={{ textAlign: "center", fontSize: "13px", fontWeight: "bold", marginBottom: "8px" }}>
                                                نسبة التأخر والالتزام الإجمالي
                                            </div>
                                            <br />
                                            <PieChart
                                                overallRate={reportData.overallRate}
                                                complianceRate={reportData.complianceRate}
                                            />
                                        </div>
                                    </div>

                                    {/* Signatures (row 2 — bottom of stats page) */}
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            marginTop: "12px",
                                            fontSize: "13px",
                                        }}
                                    >
                                        {responsible && (
                                            <div>
                                                <strong>الموظف المسؤول</strong>
                                                <br />
                                                {responsible}
                                            </div>
                                        )}
                                        {principal && (
                                            <div style={{ textAlign: "left" }}>
                                                <strong>مدير المدرسة</strong>
                                                <br />
                                                {principal}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {/* ── End printable section ── */}

                                {/* Action buttons (not printed) */}
                                <div
                                    className="text-center mt-4 no-print"
                                    style={{ display: "flex", justifyContent: "center", gap: "16px" }}
                                >
                                    <button
                                        className="btn btn-secondary"
                                        style={{ padding: "10px 24px" }}
                                        onClick={() => setStep(2)}
                                    >
                                        رجوع للجدول
                                    </button>
                                    <button
                                        className="btn btn-success"
                                        style={{ fontSize: "16px", padding: "10px 40px" }}
                                        onClick={printReport}
                                    >
                                        🖨️ طباعة التقرير
                                    </button>
                                </div>
                            </div>
                        )}

                        <hr />
                        <div className="row">
                            <RelatedLinks />
                        </div>
                    </div>
                </div>

                {/* ── Sidebar ── */}
                <div className="col-sm-12 col-md-4 no-print">
                    <SecondAds />
                    <br />
                    <MainCalculatorsList />
                </div>
            </div>

            {/* ── Info sections ── */}
            <div className="row" style={{ marginTop: "20px" }}>
                <div className="col-12 col-md-8">
                    <div className="info-sections-container">

                        <div className="info-section">
                            <div className="info-section-header">
                                <strong>كيفية استخدام تقرير متابعة تأخر الطلاب</strong>
                            </div>
                            <div className="info-section-body">
                                <ol style={{ lineHeight: "2" }}>
                                    <li>أدخل بيانات المدرسة (التعليمية والإدارة والمسؤولين)</li>
                                    <li>اختر العام الهجري والشهر المطلوب</li>
                                    <li>حدد المرحلة الدراسية، واختر الصفوف مع إدخال عدد طلاب كل صف</li>
                                    <li>اضغط <strong>عرض جدول التأخر</strong> — ستظهر أيام الدراسة تلقائياً (الأحد–الخميس)</li>
                                    <li>أدخل عدد المتأخرين لكل يوم وصف</li>
                                    <li>اضغط <strong>إنشاء التقرير</strong> للحصول على التقرير الشامل</li>
                                    <li>اضغط <strong>طباعة التقرير</strong> لطباعته أو حفظه كـ PDF</li>
                                </ol>
                            </div>
                        </div>

                        <div className="info-section">
                            <div className="info-section-header">
                                <strong>ما الذي يتضمنه التقرير؟</strong>
                            </div>
                            <div className="info-section-body">
                                <ul style={{ lineHeight: "2" }}>
                                    <li>جدول يومي بعدد المتأخرين ونسبة التأخر لكل صف</li>
                                    <li>ملخص اليوم لجميع الفصول (إجمالي التأخر والنسبة)</li>
                                    <li>الإجمالي الشهري: مجموع المتأخرين ونسبة التأخر لكل صف ولجميع الفصول</li>
                                    <li>إحصائيات: عدد الطلاب، عدد أيام الدراسة الفعلية، نسبة الالتزام، مستوى الالتزام</li>
                                    <li>رسوم بيانية: عمودي لكل صف، ودائري للنسبة الإجمالية</li>
                                    <li>خانات توقيع الموظف المسؤول ومدير المدرسة</li>
                                </ul>
                            </div>
                        </div>

                        <div className="info-section">
                            <div className="info-section-header">
                                <strong>ملاحظات مهمة</strong>
                            </div>
                            <div className="info-section-body">
                                <ul style={{ lineHeight: "2" }}>
                                    <li>أيام الدراسة تُحسب تلقائياً (من الأحد إلى الخميس) وتستثني الجمعة والسبت</li>
                                    <li>إذا كان هناك إجازات رسمية، فاتركها فارغة (ستُعامَل كـ صفر متأخر)</li>
                                    <li>نسبة التأخر = (مجموع المتأخرين) ÷ (عدد الطلاب × أيام الدراسة) × 100</li>
                                    <li>مستوى الالتزام: عالٍ (95%+)، متوسط (85%–94%)، منخفض (أقل من 85%)</li>
                                </ul>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}
